// Données de la roadmap freelance gamifiée.
// Chaque niveau contient des items. Chaque item a un `type`
// (decision | production | action) et un ou plusieurs `fields`.
// Les valeurs `prefill` sont chargées comme valeur initiale éditable.
// La validation est déclarative (voir validation.js).

export const PASSCODE = '3141';

export const XP_PER_ITEM = 10;
export const XP_LEVEL_BONUS = 50;

export const levels = [
  {
    id: 1,
    title: 'Fondations',
    subtitle: 'niche, offre, prix',
    icon: 'Sprout',
    badge: { id: 'fondations', label: 'Fondations posées' },
    items: [
      {
        id: '1.1',
        type: 'production',
        title: 'Phrase de positionnement',
        question: 'Écris ta phrase de positionnement : qui tu sers + ta promesse.',
        fields: [
          {
            key: 'phrase',
            kind: 'textarea',
            label: 'Ta phrase de positionnement',
            prefill:
              'Identité visuelle élégante pour studios, spas, instituts et lieux de bien-être haut de gamme.',
            validation: { minChars: 40 },
          },
        ],
      },
      {
        id: '1.2',
        type: 'decision',
        title: 'Segment phare',
        question:
          'Quel segment vises-tu en priorité pour démarrer, et pourquoi celui-là ?',
        fields: [
          {
            key: 'segment',
            kind: 'select',
            label: 'Segment prioritaire',
            options: [
              'Studios / spas / instituts / retraites',
              'Coachs haut de gamme (business / exécutif)',
              'Marques de bien-être / cosmétique',
            ],
            prefill: 'Studios / spas / instituts / retraites',
            validation: { required: true },
          },
          {
            key: 'pourquoi',
            kind: 'textarea',
            label: 'Pourquoi celui-là ?',
            prefill: '',
            validation: { minChars: 20 },
          },
        ],
      },
      {
        id: '1.3',
        type: 'decision',
        title: "Les 2 formules d'offre",
        question: 'Confirme tes 2 formules (2 maximum).',
        fields: [
          {
            key: 'formule1',
            kind: 'text',
            label: 'Formule 1',
            prefill: 'Logo & essentiels',
            validation: { minChars: 2 },
          },
          {
            key: 'formule2',
            kind: 'text',
            label: 'Formule 2 (offre phare)',
            prefill: 'Identité visuelle complète',
            validation: { minChars: 2 },
          },
        ],
      },
      {
        id: '1.4',
        type: 'production',
        title: 'Livrables Formule 1',
        question: 'Détaille les livrables de la Formule 1 (Logo & essentiels).',
        fields: [
          {
            key: 'livrables',
            kind: 'textarea',
            label: 'Livrables — une ligne par élément',
            prefill: `- Logo principal + 1 variante (monogramme ou version secondaire)
- Versions horizontale/verticale, couleur + noir & blanc
- Fichiers vectoriels (AI/SVG/PDF) + PNG transparents
- Mini fiche d'usage (couleurs, marges)
- 3 pistes au départ, 2 séries de révisions`,
            validation: { minLines: 3 },
          },
        ],
      },
      {
        id: '1.5',
        type: 'production',
        title: 'Livrables Formule 2',
        question:
          'Détaille les livrables de la Formule 2 (Identité visuelle complète).',
        fields: [
          {
            key: 'livrables',
            kind: 'textarea',
            label: 'Livrables — une ligne par élément',
            prefill: `Tout le contenu de la Formule 1, plus :
- Palette colorimétrique complète
- Duo typographique (titres + texte courant)
- 1 à 2 éléments graphiques / motifs signature
- Déclinaisons : carte de visite + signature mail + 2 templates réseaux sociaux
- Mini charte graphique (PDF) regroupant le tout
- Étape « univers de marque » (moodboard) en amont
- Révisions plus larges`,
            validation: { minLines: 4 },
          },
        ],
      },
      {
        id: '1.6',
        type: 'decision',
        title: 'Prix Formule 1',
        question:
          'Fixe ton prix de départ pour la Formule 1. Fourchette conseillée : 350–500 €. Plancher : ~300 € (sinon le premium n’est plus crédible).',
        fields: [
          {
            key: 'prix',
            kind: 'number',
            label: 'Prix Formule 1',
            unit: '€',
            prefill: '',
            validation: { min: 300 },
          },
        ],
      },
      {
        id: '1.7',
        type: 'decision',
        title: 'Prix Formule 2',
        question:
          'Fixe ton prix de départ pour la Formule 2. Fourchette conseillée : 900–1400 €.',
        fields: [
          {
            key: 'prix',
            kind: 'number',
            label: 'Prix Formule 2',
            unit: '€',
            prefill: '',
            validation: { gt: { itemId: '1.6', fieldKey: 'prix' } },
          },
        ],
      },
    ],
  },
  {
    id: 2,
    title: 'Portfolio',
    subtitle: 'études de cas & premier vrai projet',
    icon: 'FolderHeart',
    badge: { id: 'portfolio', label: 'Portfolio prêt' },
    items: [
      {
        id: '2.1',
        type: 'production',
        title: 'Sélection des projets',
        question:
          'Liste les 3 à 5 projets existants que tu transformeras en études de cas. Priorise les plus proches de l’univers bien-être premium.',
        fields: [
          {
            key: 'projets',
            kind: 'textarea',
            label: 'Tes projets — une ligne par projet',
            prefill: '',
            validation: { minLines: 3 },
          },
        ],
      },
      {
        id: '2.2',
        type: 'production',
        title: 'Rédiger une étude de cas',
        question: 'Rédige une étude de cas complète pour 1 projet.',
        fields: [
          {
            key: 'contexte',
            kind: 'textarea',
            label: 'Contexte (qui, secteur, situation de départ)',
            prefill: '',
            validation: { minChars: 15 },
          },
          {
            key: 'probleme',
            kind: 'textarea',
            label: 'Problème / objectif',
            prefill: '',
            validation: { minChars: 15 },
          },
          {
            key: 'solution',
            kind: 'textarea',
            label: 'Solution (les choix de design + le pourquoi)',
            prefill: '',
            validation: { minChars: 15 },
          },
          {
            key: 'resultat',
            kind: 'textarea',
            label: 'Résultat (livrables en situation + retour client si possible)',
            prefill: '',
            validation: { minChars: 15 },
          },
        ],
      },
      {
        id: '2.3',
        type: 'decision',
        title: 'Choisir le vrai client local',
        question:
          'Quel lieu réel vas-tu rebrander à tarif lancement ? Indique le nom + pourquoi il est crédible pour ta cible.',
        fields: [
          {
            key: 'nom',
            kind: 'text',
            label: 'Nom du lieu',
            prefill: '',
            validation: { minChars: 2 },
          },
          {
            key: 'justification',
            kind: 'textarea',
            label: 'Pourquoi est-il crédible pour ta cible ?',
            prefill: '',
            validation: { minChars: 15 },
          },
        ],
      },
      {
        id: '2.4',
        type: 'action',
        title: 'Cadrer le projet',
        question:
          'Quand envoies-tu le brief + le devis (tarif réduit) à ce client ? Quelle est ta 1ʳᵉ micro-étape concrète ?',
        help:
          'Cadre-le comme un vrai projet (brief, devis), pas un service entre amis.',
        fields: [
          {
            key: 'date',
            kind: 'date',
            label: 'Date d’envoi du brief + devis',
            prefill: '',
            validation: { required: true },
          },
          {
            key: 'microEtape',
            kind: 'text',
            label: '1ʳᵉ micro-étape concrète',
            prefill: '',
            validation: { minChars: 5 },
          },
        ],
      },
      {
        id: '2.5',
        type: 'production',
        title: 'Verrouiller témoignage + droit de publier',
        question:
          'Écris le canevas de témoignage à lui faire remplir + ta phrase d’autorisation de publication. À verrouiller au démarrage du projet, pas après.',
        fields: [
          {
            key: 'canevas',
            kind: 'textarea',
            label: 'Canevas témoignage + autorisation',
            prefill: `Canevas témoignage (3-4 phrases) :
- Quel était votre besoin au départ ?
- Comment s'est passée la collaboration ?
- Qu'est-ce que ça a changé / votre ressenti final ?

Autorisation : « J'autorise [Nom] à présenter ce projet (nom et visuels) dans son portfolio, son site et ses réseaux sociaux. »`,
            validation: { minChars: 60 },
          },
        ],
      },
      {
        id: '2.6',
        type: 'production',
        title: 'Mockups en situation réelle',
        question:
          'Liste les mises en situation à produire pour une étude de cas premium (enseigne du lieu, cartes, Insta, etc.).',
        fields: [
          {
            key: 'mockups',
            kind: 'textarea',
            label: 'Mises en situation — une ligne par mockup',
            prefill: '',
            validation: { minLines: 2 },
          },
        ],
      },
    ],
  },
  {
    id: 3,
    title: 'Présence',
    subtitle: 'vitrine & identité',
    icon: 'Globe',
    badge: { id: 'presence', label: 'En vitrine' },
    items: [
      {
        id: '3.1',
        type: 'decision',
        title: 'Choisir la vitrine',
        question: 'Quelle vitrine principale ? Et pourquoi ?',
        fields: [
          {
            key: 'vitrine',
            kind: 'select',
            label: 'Vitrine principale',
            options: ['Behance', 'Instagram', 'Les deux'],
            prefill: 'Behance',
            validation: { required: true },
          },
          {
            key: 'pourquoi',
            kind: 'textarea',
            label: 'Pourquoi ?',
            prefill: '',
            validation: { minChars: 15 },
          },
        ],
      },
      {
        id: '3.2',
        type: 'action',
        title: 'Page one-page',
        question:
          'Avec quel outil fais-tu ta page one-page, et pour quelle date de mise en ligne ? (Pas de site usine à gaz.)',
        fields: [
          {
            key: 'outil',
            kind: 'text',
            label: 'Outil',
            prefill: '',
            validation: { minChars: 2 },
          },
          {
            key: 'date',
            kind: 'date',
            label: 'Date de mise en ligne',
            prefill: '',
            validation: { required: true },
          },
        ],
      },
      {
        id: '3.3',
        type: 'action',
        title: 'Sa propre identité visuelle',
        question:
          'Fixe une deadline ferme et un nombre d’heures max pour créer ta propre identité. C’est ta carte de visite, pas ton chef-d’œuvre — timeboxe-la.',
        fields: [
          {
            key: 'date',
            kind: 'date',
            label: 'Date limite ferme',
            prefill: '',
            validation: { required: true },
          },
          {
            key: 'heures',
            kind: 'number',
            label: 'Heures max',
            unit: 'h',
            prefill: '',
            validation: { min: 1 },
          },
        ],
      },
    ],
  },
  {
    id: 4,
    title: 'Cadre légal',
    subtitle: 'la micro-entreprise opérationnelle',
    icon: 'Scale',
    badge: { id: 'legal', label: 'Tout en règle' },
    items: [
      {
        id: '4.1',
        type: 'action',
        title: 'Activité déclarée en BNC',
        question:
          'Vérifie que ton activité est bien déclarée en BNC (libérale). Date de vérification ?',
        fields: [
          {
            key: 'statut',
            kind: 'select',
            label: 'Statut',
            options: ['Vérifié', 'Pas encore'],
            prefill: 'Pas encore',
            validation: { equals: 'Vérifié' },
          },
          {
            key: 'date',
            kind: 'date',
            label: 'Date de vérification',
            prefill: '',
            validation: { required: true },
          },
        ],
      },
      {
        id: '4.2',
        type: 'decision',
        title: 'Déclaration URSSAF',
        question:
          'Choisis ton rythme de déclaration URSSAF et programme un rappel récurrent.',
        fields: [
          {
            key: 'rythme',
            kind: 'select',
            label: 'Rythme de déclaration',
            options: ['Mensuel', 'Trimestriel'],
            prefill: 'Mensuel',
            validation: { required: true },
          },
          {
            key: 'rappel',
            kind: 'checkbox',
            label: 'Rappel récurrent programmé',
            prefill: false,
            validation: { mustBeTrue: true },
          },
        ],
      },
      {
        id: '4.3',
        type: 'action',
        title: 'ACRE',
        question:
          'Vérifie ton éligibilité à l’ACRE et la démarche éventuelle. Date ?',
        fields: [
          {
            key: 'eligible',
            kind: 'select',
            label: 'Éligibilité',
            options: ['Oui', 'Non', 'À vérifier'],
            prefill: 'À vérifier',
            validation: { required: true },
          },
          {
            key: 'date',
            kind: 'date',
            label: 'Date',
            prefill: '',
            validation: { required: true },
          },
        ],
      },
      {
        id: '4.4',
        type: 'action',
        title: 'Compte bancaire dédié',
        question: 'Ouvre un compte bancaire dédié à l’activité. Banque + date ?',
        fields: [
          {
            key: 'banque',
            kind: 'text',
            label: 'Banque',
            prefill: '',
            validation: { minChars: 2 },
          },
          {
            key: 'date',
            kind: 'date',
            label: 'Date',
            prefill: '',
            validation: { required: true },
          },
        ],
      },
      {
        id: '4.5',
        type: 'action',
        title: 'Modèle de devis conforme',
        question: 'Prépare un modèle de devis conforme. Fait pour quelle date ?',
        fields: [
          {
            key: 'date',
            kind: 'date',
            label: 'Date',
            prefill: '',
            validation: { required: true },
          },
        ],
      },
      {
        id: '4.6',
        type: 'action',
        title: 'Modèle de facture conforme',
        question:
          'Prépare un modèle de facture conforme. Doit inclure la mention : « TVA non applicable, art. 293 B du CGI ». Date ?',
        fields: [
          {
            key: 'date',
            kind: 'date',
            label: 'Date',
            prefill: '',
            validation: { required: true },
          },
          {
            key: 'mention293b',
            kind: 'checkbox',
            label: 'Mention « 293 B » incluse',
            prefill: false,
            validation: { mustBeTrue: true },
          },
        ],
      },
      {
        id: '4.7',
        type: 'action',
        title: 'CGV simples',
        question: 'Rédige des CGV simples. Pour quelle date ?',
        fields: [
          {
            key: 'date',
            kind: 'date',
            label: 'Date',
            prefill: '',
            validation: { required: true },
          },
        ],
      },
    ],
  },
  {
    id: 5,
    title: 'Premiers clients',
    subtitle: 'prospection',
    icon: 'Send',
    badge: { id: 'clients', label: 'En chasse' },
    items: [
      {
        id: '5.1',
        type: 'production',
        title: 'Réseau chaud',
        question:
          'Liste (ou indique combien tu as listées) les 30 à 50 personnes de ton réseau chaud à contacter. La majorité des premiers clients vient de là.',
        fields: [
          {
            key: 'reseau',
            kind: 'textarea',
            label: 'Ton réseau chaud (liste ou nombre)',
            prefill: '',
            validation: { minChars: 1 },
          },
        ],
      },
      {
        id: '5.2',
        type: 'action',
        title: 'Annoncer le lancement',
        question:
          'Rédige ton message d’annonce de lancement (en demandant des mises en relation) et fixe la date d’envoi.',
        fields: [
          {
            key: 'message',
            kind: 'textarea',
            label: 'Message d’annonce',
            prefill: '',
            validation: { minChars: 40 },
          },
          {
            key: 'date',
            kind: 'date',
            label: 'Date d’envoi',
            prefill: '',
            validation: { required: true },
          },
        ],
      },
      {
        id: '5.3',
        type: 'production',
        title: 'Repérer 20 cibles',
        question:
          'Démarre la liste de ~20 entreprises de ta niche avec une identité visuelle faible. Date objectif pour finir la liste ?',
        fields: [
          {
            key: 'liste',
            kind: 'textarea',
            label: 'Début de liste',
            prefill: '',
            validation: { minChars: 5 },
          },
          {
            key: 'date',
            kind: 'date',
            label: 'Date objectif pour finir la liste',
            prefill: '',
            validation: { required: true },
          },
        ],
      },
      {
        id: '5.4',
        type: 'action',
        title: 'Contacter une par une',
        question:
          'À quel rythme contactes-tu ces entreprises (ex. X/semaine), et quelle est la date de ton 1er contact ?',
        help: 'Personnalise chaque message, jamais de copier-coller.',
        fields: [
          {
            key: 'rythme',
            kind: 'text',
            label: 'Rythme (ex. 5 / semaine)',
            prefill: '',
            validation: { minChars: 2 },
          },
          {
            key: 'date',
            kind: 'date',
            label: 'Date du 1er contact',
            prefill: '',
            validation: { required: true },
          },
        ],
      },
      {
        id: '5.5',
        type: 'production',
        title: 'Mécanique de recommandation',
        question:
          'Écris ta phrase de demande de recommandation à activer dès le 1er client satisfait.',
        fields: [
          {
            key: 'phrase',
            kind: 'textarea',
            label: 'Ta phrase de demande de recommandation',
            prefill: '',
            validation: { minChars: 30 },
          },
        ],
      },
    ],
  },
];

export const TYPE_LABELS = {
  decision: 'Décision',
  production: 'Production',
  action: 'Action',
};

// Helpers de navigation
export const getLevel = (id) => levels.find((l) => l.id === id);
export const totalItemsCount = levels.reduce((n, l) => n + l.items.length, 0);
